import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import { connect } from 'react-redux';
import { selectSignUpProperty } from '../../../redux/auth/selectors';
import { postAuthPropertyPhoto as actionPostAuthPropertyPhoto } from '../../../redux/auth/actions';

import API from '../../../services/API';
import ImageService from '../../../services/ImageService';

import Button from '../../../components/Button';
import ContactUs from '../components/ContactUs';
import InputFile from '../../../components/InputFile';
import PropertyPreview from '../components/PropertyPreview';
import Clickable from '../../../components/Clickable';
import Loader from '../../../components/Loader';

import qmStyles from '../../../theme/qmStyles';
import containerStyles from './PropertyPhoto.scss';

import googleMapImage from '../assets/art-maps.png';

class PropertyPhoto extends Component {
  static propTypes = {
    propertyData: PropTypes.object,

    postAuthPropertyPhoto: PropTypes.func.isRequired,
    navigateNext: PropTypes.func.isRequired,
  };

  static defaultProps = {
    propertyData: null,
  };

  constructor(props) {
    super(props);

    this.state = {
      loading: true,
      headerLoading: false,
      defaultPhotos: '',
    };
  }

  componentDidMount() {
    this.getPropertyPhotos();
  }

  getPropertyPhotos = () => {
    const { propertyData } = this.props;
    if (!propertyData) return;

    API.api
      .get(`/v2/properties/${propertyData.id}/imageoptions`)
      .then(({ data }) => {
        this.setState({
          defaultPhotos: data.data.default_images,
          loading: false,
        });
      })
      .catch(err => {
        console.error(err);
      });
  };

  /**
   * @param image - String
   */
  setActivePhotoType = image => {
    const { postAuthPropertyPhoto } = this.props;

    this.setState({ headerLoading: true }, () => {
      postAuthPropertyPhoto({ image_id: image }).then(() => {
        this.setState({ headerLoading: false });
      });
    });
  };

  /**
   * @param type - string
   * @returns { string, void }
   */
  getDefaultPhotoType = type => {
    const { defaultPhotos, loading } = this.state;

    if (!defaultPhotos || loading) return false;

    const selectedPhoto = defaultPhotos.filter(photo => photo.id === type);
    if (!selectedPhoto.length) return defaultPhotos[0].image;

    return selectedPhoto[0].image;
  };

  /**
   * @param image - File form data
   */
  handleFileUpload = image => {
    const { postAuthPropertyPhoto } = this.props;

    const payload = new FormData();
    payload.append('property_image', image, 'image.png');

    this.setState({ headerLoading: true }, () => {
      postAuthPropertyPhoto(payload).then(() => {
        this.setState({ headerLoading: false });
      });
    });
  };

  onNext = () => {
    const { postAuthPropertyPhoto, propertyData, navigateNext } = this.props;
    const { defaultPhotos } = this.state;

    if (!propertyData.image) {
      postAuthPropertyPhoto({ image_id: defaultPhotos[0].id }).then(
        navigateNext,
      );
    } else {
      navigateNext();
    }
  };

  renderDefaultPhotoSelection = () => {
    const { propertyData } = this.props;

    const fallbackPhoto = ImageService().getFull(
      this.getDefaultPhotoType('FALLBACK'),
    );
    const isSelected =
      propertyData.image_id === 'FALLBACK' || !propertyData.image;

    const { loading } = this.state;

    return (
      <Clickable
        onClick={() => this.setActivePhotoType('FALLBACK')}
        className={containerStyles.defaultPhotoContainer}
        style={{
          marginRight: qmStyles.gutter,
          outline: isSelected ? '3px solid #464646' : '',
        }}
      >
        {isSelected && (
          <div
            className={`qm-icon-subtask-complete ${
              containerStyles.defaultPhotoIcon
            }`}
          />
        )}
        <div className={containerStyles.defaultPhotoWrapper}>
          {loading ? <Loader /> : <img src={fallbackPhoto} alt="couch" />}
        </div>
      </Clickable>
    );
  };

  renderStreetPhotoSelection = () => {
    const { propertyData } = this.props;

    const isSelected = propertyData.image_id === 'GOOGLE';

    return (
      <Clickable
        onClick={() => this.setActivePhotoType('GOOGLE')}
        className={containerStyles.streetViewContainer}
        style={{
          marginRight: qmStyles.gutter,
          outline: isSelected ? '3px solid #464646' : '',
        }}
      >
        {isSelected && (
          <div
            className={`qm-icon-subtask-complete ${
              containerStyles.defaultPhotoIcon
            }`}
          />
        )}
        <img src={googleMapImage} alt="google-map" />
        <p className={containerStyles.streetViewText}>Use Street View</p>
        <p className={containerStyles.streetViewSubText}>Via Google Map</p>
      </Clickable>
    );
  };

  renderSelectedPhotoUrl = () => {
    const { defaultPhotos } = this.state;
    const { propertyData } = this.props;

    const image = get(propertyData, 'image');

    if (!image || !defaultPhotos)
      return ImageService().getFull(this.getDefaultPhotoType('FALLBACK'));

    const googlePhoto = defaultPhotos.filter(
      photo => image === photo.image && photo.id === 'GOOGLE',
    );

    if (googlePhoto.length) {
      return image;
    }

    return ImageService().getFull(image);
  };

  render() {
    const { propertyData } = this.props;
    const { loading, headerLoading } = this.state;

    if (!propertyData) return null;

    const inputIsSelected =
      propertyData.image_id !== 'FALLBACK' &&
      propertyData.image_id !== 'GOOGLE' &&
      propertyData.image;

    return (
      <div className={containerStyles.container}>
        <div className={containerStyles.titleContainer}>
          <div
            className={`qm-icon-subtask-complete ${containerStyles.titleIcon}`}
          />
          <h3 className={containerStyles.titleText}>Home Created!</h3>
        </div>
        <PropertyPreview
          image={this.renderSelectedPhotoUrl()}
          property={propertyData}
          loading={loading || headerLoading}
        />
        <p
          style={{
            marginTop: qmStyles.doubleGutter,
            marginBottom: qmStyles.gutter,
          }}
        >
          Select a property image
        </p>
        <div
          className={containerStyles.photoSelectionContainer}
          style={{ marginBottom: 47 }}
        >
          <div className={containerStyles.photoSelectionContent}>
            <div className={containerStyles.topPhotoSelectionContainer}>
              {this.renderDefaultPhotoSelection()}
              {this.renderStreetPhotoSelection()}
            </div>
            <InputFile
              allowedTypes={['image/jpeg', 'image/png', 'image/webp']}
              style={{ outline: inputIsSelected ? '3px solid #464646' : '' }}
              text="Add Your Photo"
              onPhotoUpload={this.handleFileUpload}
              onSelect={this.handleFileUpload}
            />
          </div>
        </div>
        <Button
          text="Continue"
          onClick={this.onNext}
          style={{
            marginBottom: qmStyles.gutter,
            backgroundColor: qmStyles.qmGreen,
            color: 'white',
            width: 360,
          }}
        />
        <ContactUs />
      </div>
    );
  }
}

const mapStateToProps = state => ({
  propertyData: selectSignUpProperty(state),
});

const bindAction = {
  postAuthPropertyPhoto: actionPostAuthPropertyPhoto,
};

export default connect(
  mapStateToProps,
  bindAction,
)(PropertyPhoto);
